import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/workplace-solutions", label: "Workplace Solutions", testid: "nav-link-workplace" },
  { to: "/residential-moving", label: "Residential Moving", testid: "nav-link-residential" },
  { to: "/industries", label: "Industries", testid: "nav-link-industries" },
  { to: "/leadership", label: "Leadership", testid: "nav-link-leadership" },
  { to: "/resources", label: "Resources", testid: "nav-link-resources" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header data-testid="header-navbar" className="fixed top-0 inset-x-0 z-50">
      <div className="hidden md:flex items-center justify-end gap-6 bg-navy text-white/80 text-xs px-8 py-1.5">
        <span className="inline-flex items-center gap-1.5">
          <MapPin size={12} className="text-brand" /> 23710 Schooler Plaza #87, Ashburn VA 20148
        </span>
        <a href="tel:+15718509694" data-testid="link-contact-phone" className="inline-flex items-center gap-1.5 hover:text-brand transition-colors">
          <Phone size={12} className="text-brand" /> (571) 850-9694
        </a>
      </div>
      <nav className="backdrop-blur-md bg-white/90 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center" onClick={() => setOpen(false)}>
            <img src="/images/logo-cropped.png" alt="HLM Moving" className="h-12 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-[13px] font-semibold tracking-wide uppercase transition-colors hover:text-brand ${
                    isActive ? "text-brand" : "text-navy/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="btn-request-quote-modal"
              className="bg-brand hover:bg-brand-hover text-white text-[13px] font-bold uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors"
            >
              Get a Quote
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-navy p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              key={location.pathname}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
            >
              <div className="px-5 py-4 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`${l.testid}-mobile`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `py-2.5 text-sm font-semibold uppercase tracking-wide border-b border-slate-100 ${
                        isActive ? "text-brand" : "text-navy/80"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/contact"
                  data-testid="btn-request-quote-mobile"
                  onClick={() => setOpen(false)}
                  className="mt-3 bg-brand text-white text-center text-sm font-bold uppercase tracking-wide px-5 py-3 rounded-sm"
                >
                  Get a Quote
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
